import React from 'react';
import {
  CircleNotch, DotsThree, MapPin, Pencil, Phone,
  Power, ShieldCheck, Trash, User,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { formatDate } from '../../lib/utils';

const RowActionsMenu = ({
  patient, checkingEligibility,
  onCheckEligibility, onToggleStatus, onEdit, onDelete,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`patient-menu-${patient.id}`}>
        <DotsThree size={18} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => onCheckEligibility(patient.id)} disabled={checkingEligibility}>
        {checkingEligibility
          ? <CircleNotch size={14} className="mr-2 animate-spin" />
          : <ShieldCheck size={14} className="mr-2" />}
        Check Eligibility
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onToggleStatus(patient)} data-testid={`toggle-status-${patient.id}`}>
        <Power size={14} className="mr-2" />
        {patient.status === 'Active' ? 'Deactivate' : 'Activate'}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onEdit(patient)}>
        <Pencil size={14} className="mr-2" /> Edit
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onDelete(patient.id)} className="text-red-600">
        <Trash size={14} className="mr-2" /> Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const PatientRow = ({
  patient, isSelected, onToggleSelect,
  onEdit, onDelete, onCheckEligibility, onToggleStatus, checkingEligibility,
}) => (
  <TableRow data-testid={`patient-row-${patient.id}`} className={isSelected ? 'bg-indigo-50' : ''}>
    <TableCell>
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onToggleSelect(patient.id)}
        data-testid={`select-patient-${patient.id}`}
      />
    </TableCell>
    <TableCell>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
          <User size={20} className="text-indigo-600" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
          <p className="sm:hidden text-xs text-slate-500 font-mono">{patient.medicaid_id}</p>
          {patient.city && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin size={12} /> {patient.city}, {patient.state}
            </p>
          )}
        </div>
      </div>
    </TableCell>
    <TableCell className="hidden sm:table-cell">
      <span className="font-mono text-sm">{patient.medicaid_id}</span>
    </TableCell>
    <TableCell className="hidden md:table-cell">{formatDate(patient.date_of_birth)}</TableCell>
    <TableCell className="hidden lg:table-cell">
      <span className="status-badge status-neutral">{patient.payer_type}</span>
      {patient.managed_care_plan && (
        <span className="ml-2 text-xs text-slate-500">({patient.managed_care_plan})</span>
      )}
    </TableCell>
    <TableCell className="hidden lg:table-cell">
      {(() => {
        const status = patient.status || 'Active';
        const isNeedsCompletion = status === 'Needs completion';
        const isActive = status === 'Active';
        const cls = isNeedsCompletion
          ? 'bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-300'
          : isActive
            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200'
            : 'bg-slate-100 text-slate-500 hover:bg-slate-100 border-slate-200';
        return (
          <Badge
            variant={isActive ? 'default' : 'secondary'}
            className={cls}
            data-testid={`patient-status-${patient.id}`}
            title={
              isNeedsCompletion
                ? 'Auto-created from a timesheet scan — needs DOB, Medicaid ID, and payer before this patient can be billed.'
                : undefined
            }
          >
            {isNeedsCompletion ? '⚠ Needs completion' : status}
          </Badge>
        );
      })()}
      {patient.eligibility_automation_enrolled && (
        <Badge
          variant="outline"
          className="ml-1 bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-50"
          title="Enrolled in the automated daily eligibility (270) sweep"
          data-testid={`patient-270-enrolled-${patient.id}`}
        >
          270 auto
        </Badge>
      )}
    </TableCell>
    <TableCell className="hidden xl:table-cell">
      {patient.phone && (
        <span className="text-sm text-slate-600 flex items-center gap-1">
          <Phone size={12} /> {patient.phone}
        </span>
      )}
    </TableCell>
    <TableCell>
      <RowActionsMenu
        patient={patient}
        checkingEligibility={checkingEligibility}
        onCheckEligibility={onCheckEligibility}
        onToggleStatus={onToggleStatus}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </TableCell>
  </TableRow>
);

export const PatientsTable = ({
  patients, loading,
  selectedIds, isAllSelected, isSomeSelected, onToggleSelectAll, onToggleSelect,
  onAddFirst, onEdit, onDelete, onCheckEligibility, onToggleStatus, checkingEligibility,
}) => (
  <Card>
    <CardHeader>
      <CardTitle style={{ fontFamily: 'Manrope' }}>Patient List ({patients.length})</CardTitle>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      ) : patients.length === 0 ? (
        <div className="text-center py-12">
          <User size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No patients found</p>
          <Button onClick={onAddFirst} variant="link" className="text-indigo-600 mt-2">
            Add your first patient
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={onToggleSelectAll}
                    data-testid="select-all-patients"
                    className={isSomeSelected ? 'opacity-60' : ''}
                  />
                </TableHead>
                <TableHead>Patient</TableHead>
                <TableHead className="hidden sm:table-cell">Medicaid ID</TableHead>
                <TableHead className="hidden md:table-cell">DOB</TableHead>
                <TableHead className="hidden lg:table-cell">Payer</TableHead>
                <TableHead className="hidden lg:table-cell">Status</TableHead>
                <TableHead className="hidden xl:table-cell">Contact</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.map((patient) => (
                <PatientRow
                  key={patient.id}
                  patient={patient}
                  isSelected={selectedIds.includes(patient.id)}
                  onToggleSelect={onToggleSelect}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCheckEligibility={onCheckEligibility}
                  onToggleStatus={onToggleStatus}
                  checkingEligibility={checkingEligibility}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

export default PatientsTable;
